import CardGrid from "@/components/cardGrid/CardGrid";
import Head from "@/components/Head";
import Icon from "@/components/icon/Icon";
import NewsPicksSelect from "@/components/newsPickSelect/NewsPickSelect";
import Pagination from "@/components/Pagination";
import { IS_PROD, REVALIDATION_TIME, SOCCER_NEWS } from "@/config/constants";
import { NAV_MENUS } from "@/config/menus";
import { useAppContext } from "@/hooks/useAppContext";
import {
  getCategoriesForArchivePage,
  getPostArchives,
  getPostArchivesByTag,
} from "@/pages/api/apiWP";
import { formatImageForSEO } from "@/utilities/image";
import { addPagesToArchive } from "@/utilities/pages";
import { formatNewsArticleSchema } from "@/utilities/seo";

const Archive = ({
  archivePath,
  archives,
  currentPage,
  seo,
  title,
  totalPages,
  tagArchives,
}) => {
  const [, category] = archivePath?.split("/category/articles/");
  const { newsPick } = useAppContext();

  const postsByTag = tagArchives?.filter(
    (archive) => archive?.tag === newsPick?.slug
  );

  const renderArchives = () => {
    if (category !== "soccer" || newsPick?.slug === "soccer") {
      return archives;
    }
    if (postsByTag?.length > 0) {
      return postsByTag[0]?.archives;
    } else {
      return postsByTag;
    }
  };
  let imagesToPreload = [];
  const archivesToShow = renderArchives()?.map((item, index) => {
    if (index < 3) {
      return { ...item, isImagePrioriy: true };
    }
    return item;
  });
  if (seo) {
    seo.newsArticleSchema = formatNewsArticleSchema(seo);
    const archiveImages = archivesToShow
      ?.filter((archive) => archive?.featuredImage?.sourceUrl)
      ?.map((archive) => archive.featuredImage?.sourceUrl)
      ?.slice(0, 4);
    imagesToPreload = formatImageForSEO(archiveImages);
  }

  return (
    <>
      <Head
        seo={seo}
        imagesToPreload={imagesToPreload}
        forceOriginalCanonical
      />
      <div className="flex items-start justify-between">
        <h1 className="mb-8 flex font-heading text-2xl font-bold leading-[1.15] text-nav-blue sm:text-[2rem]">
          <Icon
            className="mr-2 h-[32px] w-[40px]"
            height={"32px"}
            name={`${category}Category`}
            color="currentColor"
            width={"40px"}
          />
          {title}
        </h1>
        {category === "soccer" && (
          <NewsPicksSelect
            leagueParam={category}
            articleValues={{ type: "tagSlugIn", currentPage }}
            posts={tagArchives}
          />
        )}
      </div>
      <CardGrid items={archivesToShow} />
      <Pagination
        basePath={archivePath}
        currentPage={currentPage}
        totalPages={totalPages}
      />
    </>
  );
};

export async function getStaticPaths() {
  // Avoid these categories to avoid breaking the build (Soccer category is buggy)
  let paths = [];
  if (IS_PROD) {
    const avoidCategories = ["Shows", "Soccer News"];
    const menuOptions = NAV_MENUS.News.menuOptions;
    avoidCategories.forEach((category) => {
      delete menuOptions[category];
    });
    const formattedCategories = Object.values(menuOptions).map((slug) => {
      if (typeof slug === "string") {
        const cleanedSlug = slug.slice(1, -1);
        const formattedSlug = cleanedSlug?.replace(/category\/articles\//, "");
        return formattedSlug;
      } else return [];
    });

    paths = formattedCategories.flatMap((category) =>
      addPagesToArchive(category)
    );
  }
  return { paths, fallback: "blocking" };
}

export async function getStaticProps(context) {
  const params = context?.params?.archive;

  // URL param/path indexes are tracked in order to
  // handle the case of nested categories. Ex. /soccer/world-cup
  const categoryIndex = params?.[1] && params?.[1] !== "page" ? 1 : 0;
  const pagePathIndex = categoryIndex + 1;
  const pageNumberIndex = pagePathIndex + 1;

  // Fetch categories and valid archive category
  const category = params?.[categoryIndex]?.toLowerCase() || "";
  const categories = await getCategoriesForArchivePage();
  const archiveCategory = categories?.[category];

  if (!archiveCategory) {
    return { notFound: true };
  }

  // Page is a valid archive page if there is no page
  // number or url matches /page/number
  const isValidArchivePage =
    !params?.[pagePathIndex] ||
    (params?.[pagePathIndex]?.toLowerCase() === "page" &&
      params?.[pageNumberIndex]);
  // If url is not a valid archive page, redirect to
  // first page of results if search term provided or
  // return 404
  if (!isValidArchivePage) {
    return params?.[pagePathIndex]?.toLowerCase() === "page"
      ? { redirect: { destination: archiveCategory?.uri } }
      : { notFound: true };
  }

  const page = params?.[pageNumberIndex];
  // Redirect to first page if path is 0 or 1, or not a number
  if (page && (page === "0" || page === "1" || isNaN(Number(page)))) {
    return { redirect: { destination: archiveCategory?.uri } };
  }

  // Fetch archives based on category and page number
  const pageNumber = params?.[pageNumberIndex]
    ? parseInt(params?.[pageNumberIndex]) - 1
    : 0;
  const archives = await getPostArchives({
    type: "category",
    value: category,
    pageNumber,
  });

  const categoryPath =
    categoryIndex === 1 ? `${params?.[0]}/${params?.[1]}` : category;

  let tagArchives = [];
  if (params?.[0] === "soccer") {
    tagArchives = await Promise.all(
      SOCCER_NEWS.map((slug) =>
        getPostArchivesByTag({
          type: "tag",
          value: slug,
          pageNumber: pageNumber,
        })
      )
    );
  }

  return {
    props: {
      ...archives,
      ...tagArchives,
      tagArchives,
      totalPages: archives.totalPages + 1,
      archivePath: `/category/articles/${categoryPath}`,
      currentPage: params?.[pageNumberIndex]
        ? parseInt(params?.[pageNumberIndex])
        : 1,
      seo: archiveCategory?.seo,
      title: archiveCategory?.name,
    },
    revalidate: REVALIDATION_TIME,
  };
}

export default Archive;
